/**
 * Stealth-mode local storage (IndexedDB).
 *
 * When stealth is on, we never POST routines or snapshots to the server.
 * Everything lives here, in the user's browser only. Clearing browser
 * storage = a true factory reset on the user's device.
 *
 * Schema:
 *   routines:  keyPath "id"        — RoutineResponse, plus `local: true`
 *   snapshots: keyPath "id"        — Snapshot shape, plus `local: true`
 *              indexed by routine_id
 */
import { openDB } from "idb";

const DB_NAME = "trubeaute-stealth";
const DB_VERSION = 1;

let _dbp = null;
const dbp = () => {
  if (!_dbp) {
    _dbp = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("routines")) {
          db.createObjectStore("routines", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("snapshots")) {
          const s = db.createObjectStore("snapshots", { keyPath: "id" });
          s.createIndex("by_routine", "routine_id", { unique: false });
        }
      },
    });
  }
  return _dbp;
};

// ---- Routines ----
export const stealthSaveRoutine = async (routine) => {
  const db = await dbp();
  await db.put("routines", { ...routine, local: true });
  return routine;
};

export const stealthListRoutines = async () => {
  const db = await dbp();
  const all = await db.getAll("routines");
  // Newest first
  return all.sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || "")
  );
};

export const stealthGetRoutine = async (id) => {
  const db = await dbp();
  return db.get("routines", id);
};

export const stealthDeleteRoutine = async (id) => {
  const db = await dbp();
  await db.delete("routines", id);
  // Also drop snapshots tied to this routine.
  const tx = db.transaction("snapshots", "readwrite");
  const idx = tx.store.index("by_routine");
  for await (const cursor of idx.iterate(id)) {
    await cursor.delete();
  }
  await tx.done;
};

// ---- Snapshots ----
export const stealthSaveSnapshot = async (snapshot) => {
  const db = await dbp();
  const id = snapshot.id || `snap_${crypto.randomUUID()}`;
  const created_at = snapshot.created_at || new Date().toISOString();
  const stored = { ...snapshot, id, created_at, local: true };
  await db.put("snapshots", stored);
  return stored;
};

export const stealthListSnapshots = async ({ routine_id } = {}) => {
  const db = await dbp();
  const all = routine_id
    ? await db.getAllFromIndex("snapshots", "by_routine", routine_id)
    : await db.getAll("snapshots");
  return all.sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || "")
  );
};

export const stealthDeleteSnapshot = async (id) => {
  const db = await dbp();
  await db.delete("snapshots", id);
};

// ---- Bulk wipe (used by "Erase everything" + when toggling stealth off) ----
export const stealthWipeAll = async () => {
  const db = await dbp();
  const tx = db.transaction(["routines", "snapshots"], "readwrite");
  await Promise.all([tx.objectStore("routines").clear(), tx.objectStore("snapshots").clear()]);
  await tx.done;
};

export const stealthCounts = async () => {
  const db = await dbp();
  const [r, s] = await Promise.all([
    db.count("routines"),
    db.count("snapshots"),
  ]);
  return { routines: r, snapshots: s };
};
